import { useState, useRef } from 'react';
import { Box, Button, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { Draw, Keyboard } from '@mui/icons-material';

interface SignatureCaptureProps {
  onSignatureChange: (dataUrl: string | null, method: 'type' | 'draw') => void;
  disabled?: boolean;
}

export const SignatureCapture = ({ onSignatureChange, disabled = false }: SignatureCaptureProps) => {
  const theme = useTheme();
  const [signatureMethod, setSignatureMethod] = useState<'type' | 'draw'>('type');
  const [typedName, setTypedName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastPositionRef = useRef<{ x: number; y: number } | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: 'type' | 'draw') => {
    setSignatureMethod(newValue);
    if (newValue === 'type') {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    } else {
      setTypedName('');
    }
    onSignatureChange(null, newValue);
  };

  const handleTypedNameChange = (value: string) => {
    setTypedName(value);
    if (value.trim().length >= 2) {
      // Emit asynchronously to allow font to be available
      void (async () => {
        try {
          await document.fonts.load("48px 'Dancing Script'");
        } catch {
          // Fallback to cursive
        }
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 150;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, 400, 150);
        ctx.font = "48px 'Dancing Script', cursive, serif";
        ctx.fillStyle = '#000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(value.trim(), 200, 75);
        onSignatureChange(canvas.toDataURL(), 'type');
      })();
    } else {
      onSignatureChange(null, 'type');
    }
  };

  const handleStartDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    lastPositionRef.current = {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const handleDraw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing || !lastPositionRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.beginPath();
    ctx.moveTo(lastPositionRef.current.x, lastPositionRef.current.y);
    ctx.lineTo(x, y);
    ctx.strokeStyle = theme.palette.text.primary;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();

    lastPositionRef.current = { x, y };
  };

  const handleStopDrawing = () => {
    if (!isDrawing) {
      return;
    }
    setIsDrawing(false);
    lastPositionRef.current = null;

    const canvas = canvasRef.current;
    if (canvas && hasMinimumDrawing()) {
      onSignatureChange(canvas.toDataURL(), 'draw');
    } else {
      onSignatureChange(null, 'draw');
    }
  };

  const hasMinimumDrawing = (): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return false;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return false;
    }
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let filledPixels = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] > 0) {
        filledPixels++;
      }
    }
    return filledPixels > 50;
  };

  const clearSignature = () => {
    if (signatureMethod === 'type') {
      setTypedName('');
    } else {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    onSignatureChange(null, signatureMethod);
  };

  return (
    <Box>
      <Tabs value={signatureMethod} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab icon={<Keyboard />} iconPosition="start" label="Type" value="type" disabled={disabled} />
        <Tab icon={<Draw />} iconPosition="start" label="Draw" value="draw" disabled={disabled} />
      </Tabs>

      {signatureMethod === 'type' ? (
        <>
          <TextField
            fullWidth
            label="Full Name"
            placeholder="Type your full name"
            value={typedName}
            onChange={(e) => handleTypedNameChange(e.target.value)}
            sx={{ mb: 2 }}
            disabled={disabled}
          />
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              backgroundColor: 'background.paper',
              width: '100%',
              height: '150px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Dancing Script', cursive, serif",
                fontSize: '2.5rem',
                color: 'text.primary',
                userSelect: 'none',
              }}
            >
              {typedName.trim()}
            </Typography>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <canvas
            ref={canvasRef}
            width={400}
            height={150}
            style={{
              width: '100%',
              height: '150px',
              cursor: disabled ? 'default' : 'crosshair',
              display: 'block',
              touchAction: 'none',
            }}
            onMouseDown={disabled ? undefined : handleStartDrawing}
            onMouseMove={disabled ? undefined : handleDraw}
            onMouseUp={disabled ? undefined : handleStopDrawing}
            onMouseLeave={disabled ? undefined : handleStopDrawing}
            onTouchStart={disabled ? undefined : handleStartDrawing}
            onTouchMove={disabled ? undefined : handleDraw}
            onTouchEnd={disabled ? undefined : handleStopDrawing}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {signatureMethod === 'type'
            ? 'Type your full name as your legal signature'
            : 'Sign with your mouse or finger'}
        </Typography>
        <Button size="small" onClick={clearSignature} disabled={disabled}>
          Clear
        </Button>
      </Box>
    </Box>
  );
};
